import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Building2,
  Clock,
} from "lucide-react";
import { SITE } from "@/lib/site";

export default function Contact() {
  const waMessage = encodeURIComponent(
    "Hallo Care for Elektra, ik heb een vraag over jullie diensten."
  );

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-3">Contact</p>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-[#0A1128] max-w-4xl leading-[1.05]">
            Snel geholpen? Neem direct contact op.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Bel, WhatsApp of mail ons. Vaak binnen 1 werkdag antwoord — bij spoed nog sneller.
          </p>
        </div>
      </section>

      {/* CONTACT CHANNELS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          <ChannelCard
            testid="contact-call-card"
            icon={Phone}
            eyebrow="Bellen"
            title={SITE.phoneDisplay}
            description="Direct persoonlijk contact. Ma – Za, 08:00 – 20:00."
            cta="Bel nu"
            href={`tel:${SITE.phoneIntl}`}
            accent="#FF6B00"
          />
          <ChannelCard
            testid="contact-whatsapp-card"
            icon={MessageCircle}
            eyebrow="WhatsApp"
            title={SITE.phoneDisplay}
            description="Snelste route. Antwoord meestal binnen 1 uur."
            cta="Open WhatsApp"
            href={`https://wa.me/${SITE.whatsapp}?text=${waMessage}`}
            external
            accent="#25D366"
            featured
          />
          <ChannelCard
            testid="contact-email-card"
            icon={Mail}
            eyebrow="E-mail"
            title={SITE.email}
            description="Voor uitgebreide aanvragen. Reactie binnen 1 werkdag."
            cta="Stuur e-mail"
            href={`mailto:${SITE.email}`}
            accent="#0A1128"
          />
        </div>

        {/* INFO ROW */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 min-h-[220px] bg-[#0A1128]" data-testid="contact-region-card">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,107,0,0.35), transparent 40%), radial-gradient(circle at 80% 70%, rgba(37,211,102,0.15), transparent 45%)"
            }} />
            <div className="absolute inset-0" style={{
              backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "40px 40px"
            }} />
            <div className="relative p-8 text-white h-full flex flex-col justify-between min-h-[220px]">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#FF6B00]">
                <MapPin className="w-3.5 h-3.5" /> Werkgebied
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl font-bold">{SITE.region}</div>
                <p className="mt-2 text-sm text-slate-300 max-w-md">
                  {SITE.cities.join(" · ")} en alle omliggende plaatsen.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 p-8 bg-white" data-testid="contact-business-card">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-4">
              <Building2 className="w-3.5 h-3.5" /> Bedrijfsinformatie
            </div>
            <p className="font-display text-2xl font-bold text-[#0A1128]">Care for Elektra</p>
            <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-slate-500 font-semibold">KvK</dt>
              <dd className="text-[#0A1128] font-semibold">{SITE.kvk}</dd>
              <dt className="text-slate-500 font-semibold">BTW</dt>
              <dd className="text-[#0A1128] font-semibold">{SITE.btw}</dd>
              <dt className="text-slate-500 font-semibold">E-mail</dt>
              <dd><a href={`mailto:${SITE.email}`} className="text-[#0A1128] font-semibold hover:text-[#FF6B00]">{SITE.email}</a></dd>
              <dt className="text-slate-500 font-semibold">Telefoon</dt>
              <dd><a href={`tel:${SITE.phoneIntl}`} className="text-[#0A1128] font-semibold hover:text-[#FF6B00]">{SITE.phoneDisplay}</a></dd>
            </dl>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {SITE.certifications.map((c) => (
                <span key={c} className="inline-block px-2.5 py-1 rounded-full bg-orange-50 text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider">
                  {c}
                </span>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              <span>Ma – Za · 08:00 – 20:00</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ChannelCard({ icon: Icon, eyebrow, title, description, cta, href, external, accent, featured, testid }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className={`group relative flex flex-col rounded-2xl border p-8 transition-transform hover:-translate-y-1 hover:shadow-2xl ${
        featured ? "bg-[#0A1128] border-[#0A1128] text-white" : "bg-white border-slate-200 text-[#0A1128]"
      }`}
    >
      <span
        className="inline-flex items-center justify-center w-12 h-12 rounded-lg text-white mb-6"
        style={{ backgroundColor: accent }}
      >
        <Icon className="w-6 h-6" />
      </span>
      <div className={`text-xs uppercase tracking-[0.2em] font-bold mb-2 ${featured ? "text-[#FF6B00]" : "text-slate-500"}`}>
        {eyebrow}
      </div>
      <div className={`font-display text-2xl md:text-3xl font-bold break-words ${featured ? "text-white" : "text-[#0A1128]"}`}>
        {title}
      </div>
      <p className={`mt-3 text-sm leading-relaxed flex-1 ${featured ? "text-slate-300" : "text-slate-600"}`}>
        {description}
      </p>
      <span
        className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm text-white transition-colors group-hover:opacity-90"
        style={{ backgroundColor: accent }}
      >
        {cta}
      </span>
    </a>
  );
}
